import React, { useState } from "react";
import Interval from "../../models/communications/Interval";
import IntervalSetModel from "../../models/communications/IntervalSet";
import Interaction from "../../models/interactions/Interaction";
import InteractionNode from "../../models/interactions/InteractionNode";
import InteractionNodeInterval from "../../models/interactions/InteractionNodeInterval";
import { useRowSelector, useStoreDispatch, useStoreSelector } from "../../store/Hooks";
import IntervalSet from "../communications/IntervalSet";
import Pathway from "./pathway/Pathway";
import Event from "./package/Event";
import InsertionPoint from "../InsertionPoint";
import {
  compareInteractionNodesByOrdinality,
  compareIntervals,
  compareIntervalsByDay,
  getFirstOrdinalFromNodes,
  getLastOrdinalFromNodes,
  getRandomInt,
  sortIntervalsByCommunicationAndStartDay,
  stateDeepCopy,
  uniqueBy,
} from "../../helpers/DataUpdateHelper";
import Account from "./account/pages/ui/Account";
import { useDrag, DropTargetMonitor, DragSourceMonitor } from "react-dnd";
import { InteractionNodeDragItem } from "../../helpers/DragHelper";
import InteractionIntervalSetting from "../../models/communications/InteractionIntervalSetting";
import SchedulingMatrixRow from "../../models/communications/SchedulingMatrixRow";
import CommunicationSet from "../../models/communications/CommunicationSet";
import InteractionParentInterval from "../../models/interactions/InteractionParentInterval";
import NavHistoryNode from "../../models/ui/NavHistoryNode";
// import { DropTargetMonitor, useDrop, useDrag } from "react-dnd";

// NOTE: THIS IS ONLY BEING USED FOR PATHWAYS IN NODES RIGHT NOW
interface InteractionShellProps {
  interactionID: number;
  rootInteractionID: number;
  interactionNodeID?: number; // root node may not have a corresponding node
  item?: any;
  dragPreview?: boolean | null | undefined;
  childType?: string | null | undefined;
  editMode?: boolean | null | undefined;
  matrixRow?: SchedulingMatrixRow | null | undefined;
}

// let isInitial = true;
const InteractionShell = (props: InteractionShellProps) => {
  const [isInitial, setIsInitial] = useState(true);
  const isReferenceSet = false;
  const rootStyle: any = props.dragPreview
    ? { color: "red" }
    : { color: "inherit", marginTop:"5px", marginBottom:"5px" };

  const dispatch = useStoreDispatch();

  const interactionNodes = useStoreSelector(
    (state) => state.interactionTree.InteractionNodes,
  );

  const communicationSets = useStoreSelector(
    (state) => state.interactionTree.CommunicationSets,
  );

  const interactions = useStoreSelector(
    (state) => state.interactionTree.Interactions,
  );

  const intervals = useStoreSelector(
    (state) => state.interactionTree.Intervals,
  );

  const intervalSets = useStoreSelector(
    (state) => state.interactionTree.IntervalSets,
  );

  const interactionNodeIntervals = useStoreSelector(
    (state) => state.interactionTree.InteractionNodeIntervals,
  );

  const interactionParentIntervals: Array<InteractionParentInterval> =
    useStoreSelector(
      (state) => state.interactionTree.InteractionParentIntervals,
    );

  const navHistoryNodes: Array<NavHistoryNode> =
    useStoreSelector(
      (state) => state.nav.navHistory,
    );

  // parent is last node in history nodes
  const thisNodeAsRepresentedInNavHistory = navHistoryNodes[navHistoryNodes.length-1] || null

  let thisNode: InteractionNode | null = null;
  let nodeIndex = -1;

  if (props.interactionNodeID) {
    // use redux to get the metadata for THIS node
    // start by trying interaction node id passed in
    // NOTE: this wont be present if the interaction is currently "popped out"
    nodeIndex = interactionNodes.findIndex(
      (node: InteractionNode) => node.id === props.interactionNodeID,
    );
    if (nodeIndex > -1) {
      thisNode = interactionNodes[nodeIndex];
    } 
  }

  // use redux to get the metadata for THIS interaction
  const interactionIndex = interactions.findIndex(
    (interaction: Interaction) => interaction.id === props.interactionID,
  );
  if (interactionIndex === -1) {
    // If we can't find the interaction, return blank for now
    return <></>;
  }
  const thisInteraction = interactions[interactionIndex];
  const interactionNode = useRowSelector(InteractionNode, props.interactionNodeID || 0);
  const interaction = useRowSelector(Interaction, props.interactionID || 0);

  const handleDeleteInteraction =  async () => {
    // @FIXME - popped out nodes wont delete right now because nav history functionality needs to be fixed
    if (interactionNode) {
      await interactionNode?.delete();
    } 
    if(thisType == 'account'){
      await interaction?.delete();
    }
    if(!interactionNode){
      const newURL = "" + process.env.REACT_APP_URL;
      window.location.replace(newURL);
    }

  }

  let intervalSet: IntervalSetModel | null = null;
  let intervalSetIndex = null;
  if (thisInteraction.properties.intervalSetID) {
    intervalSetIndex = intervalSets.findIndex((intervalSet) => {
      return thisInteraction.properties.intervalSetID === intervalSet.id;
    });
    intervalSet = intervalSets[intervalSetIndex];
  }

  const thisType = thisInteraction.properties.type;

  // use redux to get all child nodes for this current parent
  let childNodes = interactionNodes.filter((interactionNode) => {
    return interactionNode.parent_interaction_id === props.interactionID;
  });

  // childNodes = stateDeepCopy(childNodes);
  // sort by ordinality
  childNodes = childNodes.sort(compareInteractionNodesByOrdinality);

  // array of all interaction_node_intervals corresponding to this node
  const interactionNodeIntervalsForThisInteraction: Array<InteractionNodeInterval> =
    interactionNodeIntervals.filter((interactionNodeInterval) => {
      return (
        thisNode && thisNode.id === interactionNodeInterval.interaction_node_id
      );
    });

  const interactionChildren: Array<Interaction> = [];
  let interactionNodeIntervalsForThisInteractionChildren: Array<InteractionNodeInterval> =
    [];
  const interactionNodesForThisInteractionChildren: Array<InteractionNode> =
    childNodes;

  interactionNodeIntervals.filter((interactionNodeInterval) => {
    return (
      thisNode && thisNode.id === interactionNodeInterval.interaction_node_id
    );
  });

  let associatedIntervals = intervals.filter((interval: Interval) => {
    return interval.interval_set_id == thisInteraction.properties.intervalSetID;
  });

  // sort the intervals
  associatedIntervals =
    sortIntervalsByCommunicationAndStartDay(associatedIntervals);

  const associatedCommSets = associatedIntervals.reduce(
    (accumulator: any, interval: Interval) => {
      communicationSets.forEach((communicationSet) => {
        if (interval.communication_set_id === communicationSet.id) {
          accumulator.push(communicationSet);
        }
      });
      return accumulator;
    },
    [],
  );

  // needed for creating unique indexes
  let countIndex = 0;
  // use redux to get all corresponding child interactions for this current parent
  const childInteractions = childNodes.map((childNode) => {
    // get the corresponding interaction for this current child node
    const interactionIndex = interactions.findIndex(
      (interaction: Interaction) => interaction.id === childNode.child_interaction_id,
    );
    if (interactionIndex === -1) {
      return <></>;
    }
    const thisInteraction = interactions[interactionIndex];
    const exists = interactionChildren.some((obj:any) => obj.id === thisInteraction.id);
    // dont have repeated copies of interactions in interactionChildren
    if (!exists) {
      interactionChildren.push(thisInteraction);
    }
    countIndex++
    // array of all interaction_node_intervals corresponding to this node

    // recursively render interactions/events
    if (thisType == "pathway" || thisType == "account") {
      return (
        <span key={"span" + childNode.id + countIndex}>
          <InteractionShell
            key={thisInteraction.id+countIndex} 
            interactionID={thisInteraction.id}
            rootInteractionID={props.rootInteractionID}
            interactionNodeID={childNode.id}
            childType={thisType}
          />
          <InsertionPoint
            key={thisInteraction.id + "insertionPoint"+countIndex}
            type="main"
            interactionNode={childNode}
            interaction={thisInteraction}
            intervalSetID={null}
            ordinal={childNode.ordinal}
            accounts={thisType == "account"}
          />
        </span>
      );
    } else if (thisType == "interval_set") {
      // get all associated intervals via interaction node intervals
      // get all associated interaction node intervals for this node
      const associatedInteractionNodeIntervals =
        interactionNodeIntervals.filter((interactionNodeInterval) => {
          return interactionNodeInterval.interaction_node_id === childNode.id;
        });

      interactionNodeIntervalsForThisInteractionChildren = [
        ...interactionNodeIntervalsForThisInteractionChildren,
        ...associatedInteractionNodeIntervals,
      ];

      let matrixRow: SchedulingMatrixRow | null = null;
      // let allInteractionRows: Array<SchedulingMatrixRow> = [];
      const interaction_x_interval_settings: Array<InteractionIntervalSetting> =
        [];

      associatedIntervals.forEach(function (associatedInterval: Interval) {
        // check if existing interaction/interval combo, record result either way
        let found = false;
        let interactionNodeIntervalID = -1;
        let interactionNodeIntervalCondition: number | null = null;
        let interactionNodeIntervalCommunicationSetID:
          | number
          | CommunicationSet
          | null = null;

        associatedInteractionNodeIntervals.forEach(
          function (associatedInteractionNodeInterval) {
            if (
              childNode.id ===
                associatedInteractionNodeInterval.interaction_node_id &&
              associatedInterval.id ===
                associatedInteractionNodeInterval.interval_id
            ) {
              found = true;
              if (interactionNodeIntervalID === -1) {
                interactionNodeIntervalID =
                  associatedInteractionNodeInterval.id;
              }
              if (!interactionNodeIntervalCondition) {
                interactionNodeIntervalCondition =
                  associatedInteractionNodeInterval.condition_id;
              }
              if (!interactionNodeIntervalCommunicationSetID) {
                interactionNodeIntervalCommunicationSetID =
                  associatedInteractionNodeInterval.communication_set_id;
              }
            }
          },
        );
        const associatedInteraction_ID = childNode.child_interaction_id;
        const associatedInteractionNode_ID = childNode.id;
        const associatedInterval_ID = associatedInterval.id;

        const interactionIntervalSetting = new InteractionIntervalSetting(
          interactionNodeIntervalID,
          associatedInteraction_ID,
          associatedInteractionNode_ID,
          associatedInterval_ID,
          found,
          interactionNodeIntervalCondition,
          interactionNodeIntervalCommunicationSetID,
          "",
          props.editMode || false,
        );

        interaction_x_interval_settings.push(interactionIntervalSetting);
      });
      matrixRow = new SchedulingMatrixRow(
        thisInteraction,
        childNode,
        interaction_x_interval_settings,
        childNode.condition_id,
        thisInteraction.properties.intervalSetID || -1,
        childNode.ordinal,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        associatedIntervals,
        associatedCommSets,
      );

      return (
        <InteractionShell
          key={thisInteraction.id+countIndex}
          interactionID={thisInteraction.id}
          rootInteractionID={props.rootInteractionID}
          interactionNodeID={childNode.id}
          childType={thisType}
          matrixRow={matrixRow}
        />
      );
    }
  });

  let currentSet: number = props.rootInteractionID; // this can't be -1, we should set it to be the same as the rootInteractionID?

  if (thisNode) {
    // if (thisNode.parent) {
    //   currentParent = thisNode.parent;
    // }
    if (thisNode.set_interaction_id) {
      currentSet = thisNode.set_interaction_id;
    }
  }

  if (thisType != "interval_set") {
    // If we're on an account, the parent and set for any child pathways
    //        should always just be this interactionID
    //        otherwise if this is under a node, and we're trying to add a child to that node,
    //        make the parent of that new child interaction THIS interaction
    // NOTE: in thisNodeAsRepresentedInNavHistory, the child_interaction_id for this node
    //       is actually thisNodeAsRepresentedInNavHistory.parentid 
    //       (because we need to pass into get_data_for_set() as a parent to find children)
    // NOTE: Most recently, we're just pulling the setid to use for other insertion points to be the one thats already been used
    //       Just to keep things consistent at least.
    const tempPlaceholderNodeForFirstInsertionPoint = new InteractionNode(
      123456789,
      thisType == 'account' ? props.interactionID : childNodes && childNodes.length > 0  ? childNodes[0].set_interaction_id : thisNodeAsRepresentedInNavHistory.setid,
      thisType == 'account' ? props.interactionID : interactionNode ? interactionNode?.child_interaction_id : thisNodeAsRepresentedInNavHistory.parentid,
      0,
      0,
      null,
      null,
      "",
    );
    childInteractions.unshift(
      <span key={"spanInsertAtBeginning" + props.interactionID}>
        {" "}
        <InsertionPoint
          key={"spanInsertAtBeginningInsertionPoint"}
          type="main"
          interactionNode={tempPlaceholderNodeForFirstInsertionPoint}
          interaction={thisInteraction}
          intervalSetID={null}
          ordinal={0}
          accounts={thisType == "account"}
        />
      </span>,
    );
  }

  let content = null;

  // TODO - we need to update this so it ALWAYS lazy loads its children
  // delete this once we confirm the other one works!
  const handleFetchAndProcessInteractions = async () => {
    // if the clicked pathway interaction is not the root and this is the first time its been clicked
      setTimeout(async () => {
        // find all the entire interaction tree pertaining to this root node!
        let thisNode: InteractionNode | null = null;
        //let thisParent: Interaction | null = null;
        if (props.interactionNodeID) {
          // use redux to get the metadata for THIS node
          nodeIndex = interactionNodes.findIndex(
            (node: InteractionNode) => node.id === props.interactionNodeID,
          );
          if (nodeIndex > -1) {
            thisNode = interactionNodes[nodeIndex];
          }
          // if cannot find in redux, fetch from db
          if(!thisNode){
            thisNode = await InteractionNode.findByID(props.interactionNodeID)
          }
        }
        // we only assume that pathway might have childen within a different set
        // if its a pathway with no children
          //setIsReferenceSet(true);
          if(thisNode && isInitial){
            // this node is loaded to ui, dont do it again!
            setIsInitial(false);

            // handle shared: get all child nodes with a set_interaction_id thats equal to this interaction
            // ("sharing" interactions sets the set_interaction_id for all child notes to this interaction's id)
            await Interaction.fetchAndProcessInteractions(thisInteraction.id, true, thisInteraction.id)
            
            // handle anonymous: get all child nodes with a set_interaction_id that might be part of a set
            // which has been estabilished far "above" it
            await Interaction.fetchAndProcessInteractions(thisNode.set_interaction_id, true, thisInteraction.id)
          }
        
      }, 300);
  };

  const [{ isDragging }, drag] = useDrag({
    type: "INTERACTION", // Make sure to define the type here
    item: thisNode
      ? new InteractionNodeDragItem(thisNode, thisInteraction, thisNode.ordinal)
      : new InteractionNodeDragItem(
          new InteractionNode(1, 1, 1, 1, 1, null, null, ""),
          thisInteraction,
          0,
        ), // The data you want to transfer during the drag
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  if (thisType == "account") {
    content = (
      <Account
        key={thisInteraction.id}
        handleDeleteInteraction = {handleDeleteInteraction}
        childInteractions={childInteractions}
        interaction={thisInteraction}
        rootInteractionID={props.rootInteractionID}
        node={thisNode}
      />
    );
  } else if (thisType == "pathway") {
    content = (
      <div onClick={handleFetchAndProcessInteractions}>
        <Pathway
          key={thisInteraction.id}
          handleDeleteInteraction={handleDeleteInteraction}
          childInteractions={childInteractions}
          interaction={thisInteraction}
          rootInteractionID={props.rootInteractionID}
          node={thisNode}
          isInitial={isInitial}
          isReferenceSet={isReferenceSet}
          dragPreview={props.dragPreview}
        />
      </div>
    );
  } else if (thisType == "interval_set") {
    content = (
      <div         onClick={handleFetchAndProcessInteractions}
      >      <IntervalSet
        key={thisInteraction.id}
        childInteractions={childInteractions}
        handleDeleteInteraction={handleDeleteInteraction}
        associatedIntervals={associatedIntervals}
        associatedCommSets={associatedCommSets}
        intervalSet={intervalSet || new IntervalSetModel(1, null, null, "")}
        currentParent={thisInteraction.id}
        interaction={thisInteraction}
        interactionNode={thisNode}
        currentSet={currentSet}
        associatedInteractionNodeIntervals={
          interactionNodeIntervalsForThisInteractionChildren
        }
        associatedInteractionNodes={interactionNodesForThisInteractionChildren}
        associatedInteractions={interactionChildren}
        interactionParentIntervals={interactionParentIntervals}
        isReferenceSet={isReferenceSet}
      /></div>

    );
  } else if (thisType == "cs1survey") {
    if (props.matrixRow) {
      content = (
        <Event
          ref={drag}
          key={thisInteraction.id}
          interaction={thisInteraction}
          interactionNode={thisNode}
          interactionNodeIntervals={interactionNodeIntervalsForThisInteraction}
          isInitial={isInitial}
          handleDeleteInteraction={handleDeleteInteraction}
          isReferenceSet={isReferenceSet}
          dragPreview={props.dragPreview}
          matrixRow={props.matrixRow}
        />
      );
    } else {
      content = (
        <Event
          key={thisInteraction.id}
          interaction={thisInteraction}
          interactionNode={thisNode}
          interactionNodeIntervals={interactionNodeIntervalsForThisInteraction}
          isInitial={isInitial}
          handleDeleteInteraction={handleDeleteInteraction}
          isReferenceSet={isReferenceSet}
          dragPreview={props.dragPreview}
        />
      );
    }
  } else if (thisType == "survey") {
    content = (
      <Event
        key={thisInteraction.id}
        interaction={thisInteraction}
        interactionNode={thisNode}
        interactionNodeIntervals={interactionNodeIntervalsForThisInteraction}
        isInitial={isInitial}
        handleDeleteInteraction={handleDeleteInteraction}
        isReferenceSet={isReferenceSet}
        dragPreview={props.dragPreview}
      />
    );
  } else if (thisType == "question") {
    content = (
      <Event
        key={thisInteraction.id}
        interaction={thisInteraction}
        interactionNode={thisNode}
        handleDeleteInteraction={handleDeleteInteraction}
        interactionNodeIntervals={interactionNodeIntervalsForThisInteraction}
        isInitial={isInitial}
        isReferenceSet={isReferenceSet}
        dragPreview={props.dragPreview}
      />
    );
  } else if (thisType == "information") {
    content = (
      <Event
        key={thisInteraction.id}
        interaction={thisInteraction}
        interactionNode={thisNode}
        handleDeleteInteraction={handleDeleteInteraction}
        interactionNodeIntervals={interactionNodeIntervalsForThisInteraction}
        isInitial={isInitial}
        isReferenceSet={isReferenceSet}
        dragPreview={props.dragPreview}
      />
    );
  }

  if (props.matrixRow) {
    return content;
  } else {
    return (
      <div style={rootStyle} ref={drag}>
        {!isDragging && content}
      </div>
    );
  }
};
export default InteractionShell;
